import { Stack } from '@jarvis/web-stratus-client'

/**
 * CREDENTIALS
 *
 * Credentials to consume Content Stack APIs through the client.
 * See: src/services/content-stack/helpers/create-content-stack-client.ts
 */
export const CONTENT_STACK_CREDENTIALS = {
  apiKey: 'blt8a8e334f0a83fb21',
  [Stack.pie]: {
    deliveryToken: 'cs8e4d4c476111bdad9ef3a077',
    environment: 'pie',
  },
  [Stack.stage]: {
    deliveryToken: 'cs1d8958ab4c6be46a38350bc4',
    environment: 'stage',
  },
  [Stack.prod]: {
    deliveryToken: 'csf5dd829a1a24c27be970a285',
    environment: 'prod',
  },
}

/**
 * LOCALES
 *
 * Supported and fallback locales on Content Stack.
 */
export const SUPPORTED_LOCALES = [
  'ar-sa', // Arabic - Saudi Arabia
  'bg-bg', // Bulgarian - Bulgaria
  'zh-cn', // Chinese - China
  'zh-hk', // Chinese - Hong Kong
  'zh-tw', // Chinese - Taiwan
  'hr-hr', // Croatian - Croatia
  'cs-cz', // Czech - Czech Republic
  'da-dk', // Danish - Denmark
  'nl-nl', // Dutch - Netherlands
  'en-gb', // English - United Kingdom
  'en-us', // English - United States
  'et-ee', // Estonian - Estonia
  'fi-fi', // Finnish - Finland
  'fr-fr', // French - France
  'fr-ca', // French - Canada
  'de-de', // German - Germany
  'el-gr', // Greek - Greece
  'he-il', // Hebrew - Israel
  'hu-hu', // Hungarian - Hungary
  'id-id', // Indonesian - Indonesia
  'it-it', // Italian - Italy
  'ja-jp', // Japanese - Japan
  'ko-kr', // Korean - South Korea
  'lv-lv', // Latvian - Latvia
  'lt-lt', // Lithuanian - Lithuania
  'nb-no', // Norwegian Bokmål - Norway
  'pl-pl', // Polish - Poland
  'pt-br', // Portuguese - Brazil
  'pt-pt', // Portuguese - Portugal
  'ro-ro', // Romanian - Romania
  'ru-ru', // Russian - Russia
  'sk-sk', // Slovak - Slovakia
  'sl-si', // Slovenian - Slovenia
  'es-es', // Spanish - Spain
  'sv-se', // Swedish - Sweden
  'th-th', // Thai - Thailand
  'tr-tr', // Turkish - Turkey
  'uk-ua', // Ukrainian - Ukraine
]

export const FALLBACK_LANGUAGES = {
  ar: 'ar-sa',
  bg: 'bg-bg',
  zh: 'zh-cn',
  hr: 'hr-hr',
  cs: 'cs-cz',
  da: 'da-dk',
  nl: 'nl-nl',
  en: 'en-us',
  et: 'et-ee',
  fi: 'fi-fi',
  fr: 'fr-fr',
  de: 'de-de',
  el: 'el-gr',
  he: 'he-il',
  hu: 'hu-hu',
  id: 'id-id',
  it: 'it-it',
  ja: 'ja-jp',
  ko: 'ko-kr',
  lv: 'lv-lv',
  lt: 'lt-lt',
  nb: 'nb-no',
  pl: 'pl-pl',
  pt: 'pt-pt',
  ro: 'ro-ro',
  ru: 'ru-ru',
  sk: 'sk-sk',
  sl: 'sl-si',
  es: 'es-es',
  sv: 'sv-se',
  th: 'th-th',
  tr: 'tr-tr',
  uk: 'uk-ua',
}

export const FALLBACK_LOCALES = {
  'zh-mo': 'zh-tw',
}

export const FALLBACK_LOCALE = 'en-us'

/**
 * QUERIES
 *
 * Prebuild queries for fetching specific entries in multiple entries for content-types.
 * See: src/services/content-stack/helpers/get-query-params.ts
 */

/**
 * Flow taxonomies
 */
const ENTRY = {
  key: 'taxonomies.flow.entry',
  EARLY_OOBE: Symbol('early_oobe'),
  PORTAL_OOBE: Symbol('portal_oobe'),
  PORTAL_SCAN_SETUP: Symbol('portal_scan_setup'),
  APP_OOBE: Symbol('app_oobe'),
}
const EXPERIENCE = {
  key: 'taxonomies.flow.experience',
  SMB: Symbol('smb'),
  ECP: Symbol('ecp'),
  CONSUMER: Symbol('consumer'),
}
const PROGRAM_LEVEL = {
  key: 'taxonomies.flow.program_level',
  HP_PLUS: Symbol('hp_plus'),
  STANDARD: Symbol('standard'),
  UCDE_PLUS: Symbol('ucde_plus'),
}

/**
 * Device taxonomies
 */
const BIZ_MODEL = {
  key: 'taxonomies.device.biz_model',
  E2E: Symbol('e2e'),
  FLEX: Symbol('flex'),
}
const SKU_TYPE = {
  key: 'taxonomies.device.sku_type',
  SINGLE: Symbol('single'),
  DUAL: Symbol('dual'),
}
const CARTRIDGE_TYPE = {
  key: 'taxonomies.device.cartridge_type',
  CIRCUITRY: Symbol('circuitry'),
  CHIP: Symbol('chip'),
  DEFAULT: Symbol('default'),
}
const FUNCTION_TYPE = {
  key: 'taxonomies.device.function_type',
  MFP: Symbol('mfp'),
  SFP: Symbol('sfp'),
}
const PRINTER_FORMAT = {
  key: 'taxonomies.device.printer_format',
  LARGE: Symbol('large'),
  STANDARD: Symbol('standard'),
}
const SUPPLY_TYPE = {
  key: 'taxonomies.device.supply_type',
  INK: Symbol('ink'),
  LASER: Symbol('laser'),
}
const PLATFORM_IDENTIFIER = {
  key: 'taxonomies.device.platform_identifier',
  GEN1: Symbol('gen1'),
  GEN2: Symbol('gen2'),
  GEN2_YETI: Symbol('gen2yeti'),
}

/**
 * Account taxonomies
 */
const TYPE = {
  key: 'taxonomies.account.type',
  PERSONAL: Symbol('personal'),
  BUSINESS_TRANSACTIONAL_SMB: Symbol('business_transactional_smb'),
  CHANNEL_MPS_COMPANY: Symbol('channel_mps_company'),
}
const HP_PLUS_PLAN = {
  key: 'taxonomies.account.hp_plus_plan',
  ACCEPTED: Symbol('accepted'),
  DECLINED: Symbol('declined'),
}
export const CMS_QUERIES = {
  FLOW: {
    ENTRY,
    EXPERIENCE,
    PROGRAM_LEVEL,
  },
  DEVICE: {
    BIZ_MODEL,
    SKU_TYPE,
    CARTRIDGE_TYPE,
    FUNCTION_TYPE,
    PRINTER_FORMAT,
    SUPPLY_TYPE,
    PLATFORM_IDENTIFIER,
  },
  ACCOUNT: {
    TYPE,
    HP_PLUS_PLAN,
  },
}

/**
 * ENTRIES
 *
 * Entries current available on Content Stack.
 *
 * There are two type of entries:
 *   - Multiple entries for content-type:
 *     These kind of entries are composed by multiple entries for only one content-type.
 *     Then, you should only fill the field contentTypeUid, in order to retrieve the specific
 *     entry you should provide an array with the necessary queries to filter the desired entry.
 *
 *   - Single entry for content-type:
 *     These kind of entries are composed by only one entry for a content-type.
 *     It means this entry don't need to be queried, you only have to provide the contentTypeUid
 *     and entryUid and the entry related to that will be retrieved with success once everything
 *     is set up correctly (entry created and published).
 *
 * See: src/services/content-stack/helpers/fetch-entry.ts
 */

export const ENTRIES = {
  FWU_CHOICE: {
    contentTypeUid: 'firmware_update_choice_v2',
  },
  PARTNER_LINK: {
    contentTypeUid: 'partner_link',
    entryUid: 'blt77517e874f19d767',
  },
  ACCT_VALUE_PROP: {
    contentTypeUid: 'account_value_prop',
  },
  UNRECOVERABLE_ERROR: {
    contentTypeUid: 'unrecoverable_error',
  },
  PARTNER_LINK_ERROR_MODAL: {
    contentTypeUid: 'error_modal',
    entryUid: 'blt6bae8e5d352f6856',
  },
  HP_ALL_IN_WELCOME: {
    contentTypeUid: 'hp_all_in_welcome',
    entryUid: 'blt6dcc46a8681a37fd',
  },
  RETAILER_REFURB: {
    contentTypeUid: 'account_value_prop_retailer_refurbishment',
    entryUid: 'blt6fd41cc2d5a36e27',
  },
  FWU_NOTICE: {
    contentTypeUid: 'firmware_update_notice',
  },
  CANCEL_FWU_MODAL: {
    contentTypeUid: 'cancel_firmware_update_modal',
  },
  HW_STATUS_CHECK: {
    contentTypeUid: 'hardware_status_check',
    entryUid: 'blt2be4bce232ac1047',
  },
  PORTAL_VALUE_PROP: {
    contentTypeUid: 'portal_value_prop',
  },
  CLOUD_FEATURES_MODAL: {
    contentTypeUid: 'cloud_features_modal',
  },
  CLOUD_VALUE_PROP: {
    contentTypeUid: 'cloud_value_prop',
  },
  DECLINE_PORTAL_ONBOARDING: {
    contentTypeUid: 'decline_portal_onboarding',
    entryUid: 'bltdb9f078862edf9b5',
  },
  PRINTER_ACTIVATION: {
    contentTypeUid: 'printer_activation',
  },
  GENERIC_ACTIVATION_ERROR_MODAL: {
    contentTypeUid: 'activation_error_modal',
    entriesUid: ['blt876b2f135019a7e7', 'bltf939e1f142936335'],
  },
  RETRY_GENERIC_ACTIVATION_ERROR_MODAL: {
    contentTypeUid: 'activation_error_modal',
    entriesUid: ['blt9b00debc90c25bfd', 'blta3fc1f20db5b1885'],
  },
  PAIRING_TOKEN_ACTIVATION_ERROR_MODAL: {
    contentTypeUid: 'activation_error_modal',
    entriesUid: ['bltef2a40a5861c7dd6', 'blt4b7f1999f996d625'],
  },
  USER_ACCOUNT_ACTIVATION_ERROR_MODAL: {
    contentTypeUid: 'activation_error_modal',
    entriesUid: ['blt40bae8b49a486802', 'blte19848f93fe60d6b'],
  },
  SCAN_ACTIVATION_ERROR_MODAL: {
    contentTypeUid: 'activation_error_modal',
    entryUid: 'blt30fafebfe8c55067',
  },
  PAIRING_CODE: {
    contentTypeUid: 'pairing_code_v2',
  },
  CANNOT_FIND_PAIRING_CODE_MODAL: {
    contentTypeUid: 'cannot_find_pairing_code_modal',
  },
  TOO_MANY_ATTEMPTS_ERROR_MODAL: {
    contentTypeUid: 'pairing_code_error_modal',
    entryUid: 'blt944ef6fcefb36e62',
  },
  EXPIRED_CODE_ERROR_MODAL: {
    contentTypeUid: 'pairing_code_error_modal',
    entriesUid: ['bltdf48e47d55514c5b', 'blt861c06bd871d88f5'],
  },
  DAG_DOWN_ERROR_MODAL: {
    contentTypeUid: 'pairing_code_error_modal',
    entriesUid: ['blt62a026fcb3119bed', 'blt8d2f977cb08ad738'],
  },
  WRONG_PORTAL_ERROR_MODAL: {
    contentTypeUid: 'pairing_code_error_modal',
    entriesUid: ['blt17ad9ff9129a55de', 'bltd5b6b37b186d7571'],
  },
  SUPPORTED_COUNTRIES_MODAL: {
    contentTypeUid: 'supported_countries_modal',
    entryUid: 'blt3c9cb86b9236f0ab',
  },
  HP_PLUS_REQUIREMENTS: {
    contentTypeUid: 'hp_plus_requirements',
    entryUid: 'blt06559ba1406d5c65',
  },
  PRINTER_NAME_AND_LOCATION: {
    contentTypeUid: 'printer_name_and_location',
    entryUid: 'bltf5c03853b5c0c029',
  },
  PORTAL_HANDOFF: {
    contentTypeUid: 'portal_handoff',
    entryUid: 'blt49c52f236e665a56',
  },
} satisfies Record<
  string,
  {
    contentTypeUid: string
    entryUid?: string
    entriesUid?: readonly string[]
  }
>

export type Entries = typeof ENTRIES
